import type { CSSProperties, ReactElement, ReactNode } from 'react';
import type { OptionProps } from './const';
import { cn } from '@utils';
import { Children, cloneElement, isValidElement, useEffect, useRef } from 'react';
import ScrollArea from '../ScrollArea';
import './index.less';

interface DropdownProps {
  value?: string;
  children?: ReactNode;
  onChange?: (value: string) => void;
  className?: string;
  style?: CSSProperties;
  autoScrollToSelected?: boolean;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
}

export function Dropdown(props: DropdownProps) {
  const { value = '', children, onChange, className, style, autoScrollToSelected = true, onScroll } = props;
  const viewportRef = useRef<HTMLDivElement>(null);

  // 只在下拉框打开时（组件挂载时）滚动到选中项
  useEffect(() => {
    if (!autoScrollToSelected || !value || !viewportRef.current) {
 return;
}

    // 使用 setTimeout 确保 DOM 已经渲染
    const timer = setTimeout(() => {
      const activeElement = viewportRef.current?.querySelector(`[data-value="${value}"]`);
      if (activeElement) {
        activeElement.scrollIntoView({
          block: 'center',
          inline: 'nearest',
        });
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [autoScrollToSelected]); // 只依赖 autoScrollToSelected，不依赖 value

  // 处理 children，为每个 Option 注入 active 和 onClick
  const renderChildren = () => {
    return Children.map(children, (child) => {
      if (isValidElement<OptionProps>(child)) {
        const optionValue = child.props.value;
        const isActive = value === optionValue;
        const isDisabled = child.props.disabled;

        return cloneElement(child as ReactElement<OptionProps>, {
          active: isActive,
          onClick: () => {
            if (!isDisabled && onChange) {
              onChange(optionValue);
            }
          },
        });
      }
      return child;
    });
  };

  return (
    <div className={cn('wg-select__dropdown', className)} style={style}>
      <ScrollArea
        ref={viewportRef}
        className="wg-select__scroll"
        viewportClassName="wg-select__scroll-viewport"
        onScroll={onScroll}
      >
        {renderChildren()}
      </ScrollArea>
    </div>
  );
}
